import { useCallback, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../../config/firebaseConfig';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { CARGO_ACCESS_FIELD, COMPANY_SHIPMENTS_COLLECTION } from '../../utils/companyShipments';
import { SHIPMENT_STATUS } from '../../utils/shipments';
import { CARGO_CHECK_KEY_PREFIX, readSessionFlag, writeSessionFlag } from './useMissingDocuments';

const sessionKeyFor = (user) =>
  `${CARGO_CHECK_KEY_PREFIX}:${user?.uid || 'anon'}:${
    user?.metadata?.lastSignInTime || 'session'
  }`;

// Teslim edilmiş ya da iade/sorunlu kargolar artık "bekleyen" sayılmaz
const isActive = (shipment) =>
  shipment.status !== SHIPMENT_STATUS.DELIVERED && shipment.status !== SHIPMENT_STATUS.RETURNED;

const sortTime = (shipment) =>
  new Date(shipment.expectedAt || shipment.createdAt || 0).getTime() || 0;

/**
 * Profil › Kargolarım'daki kayıtlardan (ownerId = giriş yapan çalışan) henüz
 * teslim edilmemiş olanları oturum başına bir kez getirir. Kargo sekmesini
 * göremeyen kullanıcılar için kontrol yapılmaz.
 */
const useActiveCargo = () => {
  const { employeeId, employeeData, userAuthorization, isLoading } = useUserProfile();
  const [cargo, setCargo] = useState([]);
  const [settled, setSettled] = useState(false);
  const [authUser, setAuthUser] = useState(() => auth.currentUser);

  useEffect(() => onAuthStateChanged(auth, setAuthUser), []);

  const canUseCargo =
    employeeData?.[CARGO_ACCESS_FIELD] === true ||
    userAuthorization?.includes('admin') ||
    userAuthorization?.includes('Kısıtlı Admin');

  useEffect(() => {
    if (!authUser) {
      setCargo([]);
      setSettled(false);
      return;
    }
    if (isLoading || !employeeId) return;

    if (!canUseCargo || readSessionFlag(sessionKeyFor(authUser))) {
      setCargo([]);
      setSettled(true);
      return;
    }

    let cancelled = false;
    const checkCargo = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, COMPANY_SHIPMENTS_COLLECTION), where('ownerId', '==', employeeId))
        );
        if (cancelled) return;

        const active = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(isActive)
          .sort((a, b) => sortTime(a) - sortTime(b));
        // Bekleyen kargo yoksa bu oturumda tekrar sorma
        if (active.length === 0) writeSessionFlag(sessionKeyFor(authUser));
        setCargo(active);
      } catch (error) {
        // Kargolar okunamazsa diğer hatırlatmaları bekletmemek için sessiz kal
        console.error('Kargo kontrolü başarısız:', error);
      }
      if (!cancelled) setSettled(true);
    };

    checkCargo();
    return () => {
      cancelled = true;
    };
  }, [authUser, employeeId, isLoading, canUseCargo]);

  const markShown = useCallback(() => {
    if (authUser) writeSessionFlag(sessionKeyFor(authUser));
  }, [authUser]);

  return { cargo, settled, markShown };
};

export default useActiveCargo;
